import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Wifi, Waves, Hotel as HotelIcon, ArrowRight, Filter, Check, Coffee, Car, Dumbbell, Sparkles, ThumbsUp } from "lucide-react";
import { api, money } from "@/lib/api";
import { SearchWidget } from "@/components/SearchWidget";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

const Stars = ({ n }) => (
  <div className="flex gap-0.5">{Array.from({ length: n }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}</div>
);

const AMENITIES = [
  { key: "Free WiFi", icon: Wifi }, { key: "Pool", icon: Waves }, { key: "Breakfast", icon: Coffee },
  { key: "Parking", icon: Car }, { key: "Spa", icon: Sparkles }, { key: "Gym", icon: Dumbbell },
];

const score10 = (r) => (r <= 5 ? r * 2 : r);
const scoreLabel = (s) => (s >= 9 ? "Superb" : s >= 8 ? "Very good" : s >= 7 ? "Good" : s >= 6 ? "Pleasant" : "Review score");

export default function Hotels() {
  const [params] = useSearchParams();
  const city = params.get("city") || "";
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [sort, setSort] = useState("recommended");
  const [minStar, setMinStar] = useState(0);
  const [amenityFilter, setAmenityFilter] = useState([]);
  const [priceRange, setPriceRange] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/hotels/search", { params: { city, sort, min_star: minStar } })
      .then(({ data }) => setHotels(data.results)).finally(() => setLoading(false));
  }, [city, sort, minStar]);

  const priceBounds = useMemo(() => {
    if (!hotels.length) return [0, 1000];
    const ps = hotels.map((h) => h.price_per_night);
    return [Math.floor(Math.min(...ps)), Math.ceil(Math.max(...ps))];
  }, [hotels]);
  useEffect(() => { setPriceRange(priceBounds); }, [priceBounds]);
  const range = priceRange || priceBounds;

  const filtered = useMemo(() => {
    let out = hotels.filter((h) => h.price_per_night >= range[0] && h.price_per_night <= range[1]);
    if (amenityFilter.length) out = out.filter((h) => amenityFilter.every((a) => (h.amenities || []).some((x) => x.toLowerCase().includes(a.toLowerCase().replace("free ", "")))));
    return out;
  }, [hotels, range, amenityFilter]);

  return (
    <div>
      <div className="bg-gradient-to-br from-brand to-[#7a0046] grain relative pt-8 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto"><SearchWidget defaultTab="hotels" /></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-14 relative">
        <div className="grid lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-24">
              <div className="flex items-center gap-2 font-heading font-bold text-slate-950 mb-4"><Filter className="w-4 h-4" />Filter by</div>

              {/* Price */}
              <div className="mb-5 pb-5 border-b border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Price / night</span>
                  <span className="text-xs font-bold text-slate-700">{money(range[0])} – {money(range[1])}</span>
                </div>
                <Slider data-testid="hotel-price-slider" value={range} min={priceBounds[0]} max={priceBounds[1]} step={5} onValueChange={setPriceRange} />
              </div>

              {/* Star rating */}
              <div className="mb-5 pb-5 border-b border-slate-100">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Star rating</div>
                <div className="flex flex-wrap gap-2">
                  {[0, 3, 4, 5].map((s) => (
                    <button key={s} onClick={() => setMinStar(s)} data-testid={`star-filter-${s}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${minStar === s ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600 hover:border-brand"}`}>
                      {s === 0 ? "Any" : `${s}★+`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div className="mb-5 pb-5 border-b border-slate-100">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Facilities</div>
                <div className="space-y-2.5">
                  {AMENITIES.map((a) => (
                    <label key={a.key} className="flex items-center gap-3 cursor-pointer" data-testid={`amenity-${a.key.replace(/\s/g, "-").toLowerCase()}`}>
                      <Checkbox checked={amenityFilter.includes(a.key)} onCheckedChange={(c) => setAmenityFilter((p) => c ? [...p, a.key] : p.filter((x) => x !== a.key))} />
                      <a.icon className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-700">{a.key}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Sort by</div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="hotel-sort"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand">
                <option value="recommended">Our top picks</option>
                <option value="price_low">Price: low to high</option>
                <option value="price_high">Price: high to low</option>
                <option value="rating">Guest rating</option>
              </select>
            </div>
          </aside>

          <div className="lg:col-span-3">
            <h1 className="font-heading font-black text-2xl text-slate-950 mb-1">{city ? `Hotels in ${city}` : "All hotels"}</h1>
            <p className="text-sm text-slate-500 mb-4">{loading ? "Finding stays…" : `${filtered.length} of ${hotels.length} properties`}</p>
            <div className="space-y-4">
              {loading ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />) :
                filtered.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                    <HotelIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No hotels match your filters. Try adjusting price or facilities.</p>
                  </div>
                ) : filtered.map((h, i) => {
                  const s = score10(h.rating);
                  const hasBreakfast = (h.amenities || []).some((a) => a.toLowerCase().includes("breakfast"));
                  return (
                  <motion.div key={h.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.04 }} data-testid={`hotel-card-${i}`}>
                    <Link to={`/hotels/${h.id}`} className="group grid sm:grid-cols-[280px_1fr] bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-brand/30 transition-all">
                      <div className="relative h-52 sm:h-full overflow-hidden">
                        <img src={h.image} alt={h.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        {h.featured && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-brand text-white text-xs font-bold">Featured</span>}
                      </div>
                      <div className="p-5 flex flex-col">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <Stars n={h.star_rating} />
                            <h3 className="font-heading font-bold text-lg text-slate-950 mt-1 leading-snug group-hover:text-brand transition-colors">{h.name}</h3>
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1"><MapPin className="w-3.5 h-3.5" />{h.address}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="flex items-center justify-end gap-2">
                              <div className="leading-tight"><div className="text-sm font-bold text-slate-950">{scoreLabel(s)}</div><div className="text-[11px] text-slate-400">{h.reviews_count} reviews</div></div>
                              <div className="inline-flex items-center justify-center bg-brand text-white rounded-lg rounded-tr-none w-10 h-10 text-base font-heading font-black">{s.toFixed(1)}</div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(h.amenities || []).slice(0, 4).map((a) => (
                            <span key={a} className="text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                              {a.includes("WiFi") ? <Wifi className="w-3 h-3" /> : a.includes("Pool") ? <Waves className="w-3 h-3" /> : a.includes("Breakfast") ? <Coffee className="w-3 h-3" /> : null}{a}
                            </span>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs font-semibold">
                          <span className="flex items-center gap-1 text-emerald-600"><Check className="w-3.5 h-3.5" /> Free cancellation</span>
                          {hasBreakfast && <span className="flex items-center gap-1 text-emerald-600"><Coffee className="w-3.5 h-3.5" /> Breakfast included</span>}
                          <span className="flex items-center gap-1 text-emerald-600"><ThumbsUp className="w-3.5 h-3.5" /> No prepayment needed</span>
                        </div>
                        <div className="mt-auto pt-4 flex items-end justify-between">
                          <div><span className="text-xs text-slate-400">per night from</span><div className="font-heading font-black text-2xl text-slate-950">{money(h.price_per_night)}</div><div className="text-[11px] text-slate-400">incl. taxes & fees</div></div>
                          <span className="rounded-full bg-brand text-white font-bold text-sm px-5 py-2.5 flex items-center gap-1 group-hover:gap-2 transition-all">See availability <ArrowRight className="w-4 h-4" /></span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
